import { skipToken, useQuery } from "@tanstack/react-query";

import { getOrgTree } from "./api/organization-api";
import type { OrgUnit } from "./types";

interface UseOrgTreeReturn {
  tree: OrgUnit | null;
  isLoading: boolean;
  error: string | null;
}

export const orgTreeKey = (orgUnitId: string | undefined): readonly unknown[] =>
  ["organization", "tree", orgUnitId] as const;

/**
 * The org unit tree for the org currently on screen.
 *
 * The org id is part of the KEY, which is what makes a tree unrenderable under
 * the wrong org name. Switching org changes `orgUnitId` during render, so from
 * that render on the read is a different query with no data yet; the previous
 * org's tree is not reachable from this hook again. The page used to store the
 * tree next to the org it was loaded for and mask it at render time to close
 * the same window, because the reload ran from an effect after paint and no
 * request counter can reject a response in a frame where no later request has
 * started yet.
 */
export function useOrgTree(orgUnitId: string | undefined): UseOrgTreeReturn {
  const query = useQuery({
    queryKey: orgTreeKey(orgUnitId),
    queryFn: orgUnitId === undefined ? skipToken : () => getOrgTree(orgUnitId),
  });

  const hasOrg = orgUnitId !== undefined;

  return {
    tree: query.data ?? null,
    /* `isPending` (nothing to show yet), not `isFetching` (revalidating), and
       gated on there being an org: a query with no `queryFn` stays pending
       forever, so clearing the active org mid-flight would otherwise strand the
       skeleton and let loading outrank the no-organization empty state. */
    isLoading: hasOrg && query.isPending,
    /* An error describes having nothing to show, not a failed revalidation. v5
       keeps `data` and sets `isError` when a refetch fails, and the page renders
       the error INSTEAD of the tree, so surfacing that would replace a painted
       tree with a banner on one dropped request. */
    error:
      hasOrg && query.isError && query.data === undefined
        ? "Failed to load organization tree"
        : null,
  };
}
